import { Center, Group, SegmentedControl } from "@mantine/core";
import { useDebouncedValue, useMediaQuery } from "@mantine/hooks";
import {
  IconCalendarWeek,
  IconList,
  IconMapPlus,
  IconPlus,
} from "@tabler/icons-react";
import { useContext, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useGetEventLocations } from "../api/event";
import { useGetSessions } from "../api/session";
import Search from "../components/Sessions/Search";
import SessionFilters from "../components/Sessions/SessionFilters";
import SessionsCalendar from "../components/Sessions/Calendar";
import SessionsCards from "../components/Sessions/SessionsCards";
import SessionsTable from "../components/Sessions/SessionsTable";
import { useSetActionButtons } from "../services/ActionButtonsContext";
import { AuthContext } from "../services/context";
import {
  getCurrentWorkspace,
  isCurrentEventAdmin,
} from "../services/userConfig";

function Sessions() {
  const actions = useMemo(
    () => [
      {
        label: "Locations",
        icon: <IconMapPlus />,
        navigate: "./sessions/locations",
      },
      {
        label: "Create",
        icon: <IconPlus />,
        navigate: "./sessions/create",
      },
    ],
    []
  );

  useSetActionButtons(actions);

  const { user } = useContext(AuthContext);
  // Read matchMedia synchronously so the first render doesn't fire the
  // desktop calendar's unpaginated fetch on mobile
  const smallScreen = useMediaQuery("(max-width: 640px)", undefined, {
    getInitialValueInEffect: false,
  });
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({
    columnAccessor: "starting",
    direction: "asc",
  });
  const [searchParams, setSearchParams] = useSearchParams();

  const readFilter = (key) =>
    searchParams.get(key) ? searchParams.get(key).split(",") : [];

  const locationFilter = readFilter("locationFilter");
  const speakerFilter = readFilter("speakerFilter");
  const tagFilter = readFilter("tagFilter");

  const searchValue = searchParams.get("search") || "";
  const view = searchParams.get("view") === "calendar" ? "calendar" : "list";

  const [debouncedSearch] = useDebouncedValue(searchValue, 300);

  //search filter
  const handleSearchChange = (e) => {
    setPage(1);
    setSearchParams((searchParams) => {
      const v = e.target.value;
      if (!v) searchParams.delete("search");
      else searchParams.set("search", v);
      return searchParams;
    });
  };

  //location, speaker and tag filters, each a comma-separated list of ids
  const handleChangeFilter = (key, values) => {
    setPage(1);
    setSearchParams((searchParams) => {
      if (values.length === 0) searchParams.delete(key);
      else searchParams.set(key, values.join(","));
      return searchParams;
    });
  };

  // The sessions table drives the location filter from its own column header
  const handleChangeLocation = (values) =>
    handleChangeFilter("locationFilter", values);

  //calendar/list switch
  const handleViewChange = (nextView) => {
    setSearchParams((searchParams) => {
      if (nextView === "list") searchParams.delete("view");
      else searchParams.set("view", nextView);
      return searchParams;
    });
  };

  const currentWorkspace = getCurrentWorkspace(user);
  const eventId = currentWorkspace.eventId;
  const workspaceId = currentWorkspace.id;

  const isCalendar = !smallScreen && view === "calendar";

  const hasActiveFilters =
    Boolean(debouncedSearch) ||
    [locationFilter, speakerFilter, tagFilter].some(
      (filter) => filter.length > 0
    );

  // The calendar needs every session of the event, so it fetches without
  // pagination (the API returns a flat array when no page is passed)
  const sessionParams = smallScreen
    ? { eventId, workspaceId, sort }
    : isCalendar
      ? {
          eventId,
          workspaceId,
          sort: { columnAccessor: "starting", direction: "asc" },
          locationFilter: locationFilter.join(","),
          speakerFilter: speakerFilter.join(","),
          // The API wants every selected tag present, as a JSON array
          filterByTags: tagFilter.length > 0 ? tagFilter.map(Number) : null,
          searchParams: debouncedSearch,
        }
      : {
          page,
          eventId,
          workspaceId,
          sort,
          locationFilter: locationFilter.join(","),
          searchParams: debouncedSearch,
        };

  // Keeping previous data stops the calendar from collapsing into a loader
  // on every search keystroke or filter change. Only the flat (unpaginated)
  // shape is kept — switching over from the list view must not leak the
  // paginated response into the calendar.
  const { data, isPending: isLoading } = useGetSessions(
    sessionParams,
    isCalendar
      ? {
          placeholderData: (previousData) =>
            Array.isArray(previousData) ? previousData : undefined,
        }
      : {}
  );
  const { data: eventLocations } = useGetEventLocations(eventId);

  const mappedLocations = eventLocations
    ?.filter((event) => event.sessionCount !== 0) //don't show locations with 0 sessions
    .map(
      (event) =>
        ({
          value: String(event.id),
          label: event.name,
        }) ?? []
    );

  if (smallScreen) {
    return (
      <>
        <Search value={searchValue} onChange={handleSearchChange} />
        <SessionsCards
          data={data}
          isLoading={isLoading}
          enableActions={isCurrentEventAdmin(user)}
        />
      </>
    );
  }

  return (
    <>
      <Group justify="space-between" align="flex-end" mb="md" gap="sm">
        <Group align="flex-end" gap="sm">
          <Search
            value={searchValue}
            onChange={handleSearchChange}
            marginBottom={0}
          />
          {isCalendar && (
            <SessionFilters
              eventId={eventId}
              locations={mappedLocations ?? []}
              filters={{ locationFilter, speakerFilter, tagFilter }}
              onChange={handleChangeFilter}
            />
          )}
        </Group>
        <SegmentedControl
          value={view}
          onChange={handleViewChange}
          data={[
            {
              value: "calendar",
              label: (
                <Center style={{ gap: 6 }}>
                  <IconCalendarWeek size={16} />
                  <span>Calendar</span>
                </Center>
              ),
            },
            {
              value: "list",
              label: (
                <Center style={{ gap: 6 }}>
                  <IconList size={16} />
                  <span>List</span>
                </Center>
              ),
            },
          ]}
        />
      </Group>
      {isCalendar ? (
        <SessionsCalendar
          sessions={data}
          isLoading={isLoading}
          eventId={eventId}
          enableActions={isCurrentEventAdmin(user)}
          eventLocations={eventLocations}
          hasActiveFilters={hasActiveFilters}
        />
      ) : (
        <SessionsTable
          data={data}
          isLoading={isLoading}
          enableActions={isCurrentEventAdmin(user)}
          page={page}
          setPage={setPage}
          sortStatus={sort}
          setSort={setSort}
          eventId={eventId}
          location={locationFilter}
          changeLocation={handleChangeLocation}
          eventLocations={mappedLocations}
        />
      )}
    </>
  );
}

export default Sessions;
